import React from 'react'
import { Listbox } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { cn } from './utils/cn'
import {
  PHONE_COUNTRIES,
  splitPhone,
  type PhoneCountryCode,
} from '../../constants/phone'

export interface PhoneInputProps {
  label?: string
  error?: string
  helperText?: string
  disabled?: boolean
  required?: boolean
  id?: string
  /** E.164 or legacy stored value; parsed via splitPhone for prefill */
  value: string
  /** Emits '+60…'/'+65…' + digits-as-typed (not yet fully validated), or '' when local is empty */
  onChange: (value: string) => void
  defaultCountry?: PhoneCountryCode
  /** Restrict the selectable countries (defaults to all) */
  countries?: PhoneCountryCode[]
  className?: string
}

function deriveCountryAndLocal(
  value: string,
  defaultCountry: PhoneCountryCode
): { country: PhoneCountryCode; local: string } {
  if (!value) return { country: defaultCountry, local: '' }
  const parsed = splitPhone(value)
  if (parsed) return parsed
  if (value.startsWith('+')) {
    // Partial E.164 while typing — derive the country from the dial-code prefix
    if (value.startsWith('+65')) return { country: 'SG', local: value.slice(3).replace(/\D/g, '') }
    if (value.startsWith('+60')) return { country: 'MY', local: value.slice(3).replace(/\D/g, '') }
    return { country: defaultCountry, local: '' }
  }
  return { country: defaultCountry, local: value.replace(/\D/g, '') }
}

// Tolerate users/tests pasting a full E.164 or dial-coded value into the local
// input: "+60123456789" / "60123456789" / "0123456789" all become "0123456789"
// for MY; "+6581234567" / "6581234567" become "81234567" for SG.
function toLocalDigits(iso2: PhoneCountryCode, raw: string): string {
  let digits = raw.replace(/\D/g, '')
  const dial = PHONE_COUNTRIES[iso2].dialCode
  if (digits.startsWith(dial)) digits = digits.slice(dial.length)
  if (iso2 === 'MY') digits = `0${digits.replace(/^0+/, '')}` // exactly one trunk 0
  if (iso2 === 'SG') digits = digits.replace(/^0+/, '') // SG has no trunk 0
  return digits
}

function composeE164(iso2: PhoneCountryCode, localDigits: string): string {
  let digits = localDigits.replace(/\D/g, '')
  if (iso2 === 'MY') digits = digits.replace(/^0+/, '') // drop trunk 0
  return digits ? `+${PHONE_COUNTRIES[iso2].dialCode}${digits}` : ''
}

// Flat rectangular flags from flagcdn — straight-edged like eBay's, unlike
// emoji flags which render wavy on some platforms (or as letter pairs on Windows).
function flagUrl(iso2: PhoneCountryCode): string {
  return `https://flagcdn.com/w40/${iso2.toLowerCase()}.png`
}

/**
 * Split phone input: a country picker (flag + dial code) plus a local
 * number field. Emits E.164-shaped strings so downstream validation sees
 * exactly what the server accepts.
 *
 * Malaysian local input keeps its trunk 0 (0123456789); Singapore has none
 * (81234567). The trunk 0 is stripped when composing the E.164 value.
 */
const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      disabled,
      required,
      id,
      value,
      onChange,
      defaultCountry = 'MY',
      countries,
    },
    ref
  ) => {
    const inputId = id || `phone-${React.useId()}`
    const available = countries ?? (Object.keys(PHONE_COUNTRIES) as PhoneCountryCode[])

    const [{ country, local }, setState] = React.useState(() =>
      deriveCountryAndLocal(value, defaultCountry)
    )

    // Resync only on EXTERNAL value changes (e.g. profile load). When the
    // incoming value is the echo of what we just emitted, keep our internal
    // {country, local} — otherwise partial typing would re-derive and clear
    // the field (e.g. the transient "0" of a Malaysian trunk prefix).
    React.useEffect(() => {
      if (value === composeE164(country, local)) return
      setState(deriveCountryAndLocal(value, defaultCountry))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    const config = PHONE_COUNTRIES[country]

    const handleCountryChange = (iso2: PhoneCountryCode) => {
      // Renormalize for the new country (MY gains a trunk 0, SG loses it)
      const next = toLocalDigits(iso2, local)
      setState({ country: iso2, local: next })
      onChange(composeE164(iso2, next))
    }

    const handleLocalChange = (raw: string) => {
      const digits = toLocalDigits(country, raw)
      setState({ country, local: digits })
      onChange(composeE164(country, digits))
    }

    return (
      <div className="space-y-2">
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-neutral-700">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
        )}
        <div className="flex">
          <Listbox value={country} onChange={handleCountryChange} disabled={disabled}>
            {({ open }) => (
              <div className="relative">
                <Listbox.Button
                  aria-label="Country code"
                  className="flex h-10 items-center gap-1.5 rounded-l-md border border-r-0 border-neutral-300 bg-neutral-50 px-2 text-sm text-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <img
                    src={flagUrl(country)}
                    alt=""
                    className="h-3.5 w-5 shrink-0 rounded-[2px] object-cover"
                  />
                  <span>{config ? `+${config.dialCode}` : '+60'}</span>
                  <ChevronDownIcon
                    className={`h-3.5 w-3.5 shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </Listbox.Button>
                <Listbox.Options className="absolute left-0 z-20 mt-1 min-w-full overflow-auto rounded-md border border-neutral-200 bg-white py-1 shadow-lg focus:outline-none">
                  {available.map((iso2) => {
                    const c = PHONE_COUNTRIES[iso2]
                    return (
                      <Listbox.Option
                        key={iso2}
                        value={iso2}
                        className={({ active }) =>
                          cn(
                            'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-neutral-700',
                            active && 'bg-primary-50 text-primary-900'
                          )
                        }
                      >
                        {({ selected }) => (
                          <>
                            <img
                              src={flagUrl(iso2)}
                              alt={c.label}
                              className="h-3.5 w-5 shrink-0 rounded-[2px] object-cover"
                            />
                            <span>+{c.dialCode}</span>
                            {selected && (
                              <CheckIcon className="ml-auto h-4 w-4 text-primary-600" aria-hidden="true" />
                            )}
                          </>
                        )}
                      </Listbox.Option>
                    )
                  })}
                </Listbox.Options>
              </div>
            )}
          </Listbox>
          <input
            type="tel"
            inputMode="numeric"
            id={inputId}
            value={local}
            onChange={(e) => handleLocalChange(e.target.value)}
            placeholder={config?.localExample ?? '012 345 6789'}
            disabled={disabled}
            className={cn(
              'h-10 w-full rounded-r-md border border-neutral-300 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-neutral-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-red-500 focus-visible:ring-red-500',
              className
            )}
            ref={ref}
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {helperText && !error && <p className="text-sm text-neutral-500">{helperText}</p>}
      </div>
    )
  }
)
PhoneInput.displayName = 'PhoneInput'

export { PhoneInput }
